import React from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Wordmark } from "@trusty/shared";
import { FONT_HEADING, FONT_BODY, RADIUS, getDashboardBackground, getNavLinkStyle } from "../styles";
import { DeletionReceipt } from "../lib/accountDeletion";
import { supportMailto } from "../lib/supportMailto";
import { SUPPORT_EMAIL } from "../constants";

interface AccountDeletedPageProps {
  deletedPlanInfo: DeletionReceipt;
  isDark: boolean;
  theme: Theme;
  onReturnToLogin: () => void;
}

/**
 * ENG-3341: this page used to say "Your protection will remain active until <period end>" and
 * "contact support within 7 days". Neither was true, and the second gave no address:
 *
 *  - `DELETE /account` cancels a live Stripe subscription with `Subscription.cancel()`, which ends it
 *    IMMEDIATELY (not at period end), then deletes the subscription rows. `currentPeriodEnd` is the
 *    end of the period that was paid for, not a date anything runs until — so it is not shown.
 *  - Protection is not tied to the subscription at all; it follows sign-in. The deletion sends
 *    `logout-complete` to every Haven extension detected in this browser (DashboardLayout), and a
 *    signed-out extension protects nothing ("Sign in to turn on Haven").
 *  - No code path restores a deleted account. The user row is only marked deleted, but memberships
 *    and any organization deleted with it are gone, and there is no 7-day window anywhere.
 *
 * ENG-3369: "you won't be charged again" is shown only when the server says a live subscription WAS
 * cancelled. It used to follow `plan`, which the server set before attempting the cancel, and a
 * failed cancel was only logged — so the sentence could sit over a subscription still billing. A
 * failed cancel now stops the deletion, and this page is never reached.
 *
 * ENG-3405: the server now deletes the Firebase user, which signs out every device, not just this
 * browser. Other devices hold an ID token that lives up to an hour, hence "within an hour".
 */
export const AccountDeletedPage: React.FC<AccountDeletedPageProps> = ({
  deletedPlanInfo,
  isDark,
  theme,
  onReturnToLogin,
}) => {
  const hadPaidSubscription = deletedPlanInfo.subscriptionCancelled;
  const navLinkStyle = getNavLinkStyle(theme);
  // ENG-3470: no organization to name here — the account (and any membership) is already deleted.
  const mailtoHref = supportMailto({ page: "Account deleted" });

  return (
    <Box sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative",
      background: getDashboardBackground(theme),
      backgroundColor: theme.palette.background.default,
    }}>
      <Box sx={{
        position: 'absolute',
        top: 20,
        right: 24,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
      }}>
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); onReturnToLogin(); }}
          style={navLinkStyle}
        >
          Sign in
        </a>
        <a
          href="https://starthaven.com"
          style={navLinkStyle}
        >
          Homepage
        </a>
      </Box>
      <Container maxWidth="sm">
        <Box mb={4} display="flex" justifyContent="center">
          <Box sx={{ width: "180px" }}><Wordmark fill={isDark ? '#FFFFFF' : undefined} /></Box>
        </Box>
        <Paper elevation={0} sx={{
          p: 5, borderRadius: RADIUS.card, textAlign: "center",
          backgroundColor: theme.palette.background.paper, boxShadow: isDark ? "0 20px 60px rgba(0,0,0,0.3)" : "0 20px 60px rgba(0, 0, 0, 0.08), 0 8px 16px rgba(0, 0, 0, 0.04)",
        }}>
          <Typography variant="h4" component="h1" sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: "24px", mb: 2, color: theme.palette.primary.main }}>
            Your account has been deleted
          </Typography>
          {hadPaidSubscription && (
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: "15px", color: theme.palette.text.secondary, mb: 2 }}>
              Your paid subscription was cancelled immediately, so you won&rsquo;t be charged again.
            </Typography>
          )}
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: "15px", color: theme.palette.text.secondary, mb: 2 }}>
            {deletedPlanInfo.signedOutEverywhere
              ? "You\u2019re signed out of Haven in this browser, and on your other devices within an hour, so Haven no longer protects you."
              : "You\u2019re signed out of Haven in this browser, so Haven no longer protects you here."}
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: "13px", color: theme.palette.text.secondary, mt: 3 }}>
            Questions? Email{" "}
            <Box
              component="a"
              href={mailtoHref}
              sx={{ color: theme.palette.primary.main, textUnderlineOffset: "2px" }}
            >
              {SUPPORT_EMAIL}
            </Box>
            .
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};
